import {Component, computed, effect, ElementRef, inject, signal, Signal, viewChild} from '@angular/core';
import {TranslatePipe} from "@ngx-translate/core";
import {
  CompanyHandlingForm,
  newCompanyHandlingForm,
  newUserHandlingForm,
  UserHandlingForm
} from "./forms/user-handling.forms";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {InputComponent} from "../../../shared/form-elements/input/input/input.component";
import {SelectComponent, SelectOption} from "../../../shared/form-elements/select/select/select.component";
import {CheckboxComponent} from "../../../shared/component/checkbox/checkbox.component";
import {HttpClient} from "@angular/common/http";
import {toSignal} from "@angular/core/rxjs-interop";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {LoaderElementComponent} from "../../../shared/loader-element/loader-element.component";
import {userHandlingStore} from "./store/user-handling.store";
import {authStore} from "../../../core/service/auth/auth.store";
import {companyListingStore} from "../../../shared/store/company-listing.store";
import {translate} from "../../../shared/translate/translate";
import {NgClass} from "@angular/common";

@Component({
  selector: 'app-user-handling',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    CheckboxComponent,
    NgClass,
    RouterLink
  ],
  templateUrl: './user-handling.component.html',
  styleUrl: './user-handling.component.scss'
})
export class UserHandlingComponent {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly userHandlingStore = inject(userHandlingStore);

  private readonly removeCompanyKey= translate('GLOBAL.COMPANY.REMOVE');
  private readonly newCompanyKey = translate('GLOBAL.COMPANY.NEW');

  protected readonly roles: Signal<any[]> = toSignal(inject(HttpClient).get<any[]>('/api/loginUser/role'));
  protected readonly hasAdminAuth = inject(authStore).hasAuthority('ROLE_EMPLOYEE_ADMIN');
  private readonly companies: Signal<any> = inject(companyListingStore).provideCompanies();
  protected readonly companyOptions: Signal<SelectOption[]> = computed(() =>[
      {value: -1, label: this.removeCompanyKey()},
      {value: 0, label: this.newCompanyKey()},
      ...this.companies().map((company: any) => ({value: company.id, label: company.name}))
  ]);

  protected readonly companyOptionsFiltered = computed(()=> this.userHandlingFormChanges()?.company ? this.companyOptions() : this.companyOptions().filter(i => i.value !== -1))

  protected readonly action = this.activatedRoute.snapshot.data["action"];
  protected readonly actionKey = computed(() => this.action === 'create' ? 'NEW_USER' : 'EDIT_USER');

  protected readonly submitTried = signal(false);
  protected readonly userHandlingForm: FormGroup<UserHandlingForm> = newUserHandlingForm(this.userHandlingStore.user(), this.action);
  protected readonly userHandlingFormChanges = toSignal(this.userHandlingForm.valueChanges, {initialValue: this.userHandlingForm.value});
  protected readonly newCompany =computed(()=>
      typeof this.userHandlingFormChanges()?.company !== 'number' ? this.userHandlingForm.controls?.company as FormGroup<CompanyHandlingForm>: null
  );
  protected readonly companySelect= viewChild<ElementRef<SelectComponent>>('companySelect');
  protected readonly initialCompanySelection = this.userHandlingForm.controls?.company?.value as any;

  //only in registeration
  protected readonly checkBox = signal(false);

  protected readonly inputPlaceHolder = translate('GLOBAL.INPUT.PLACEHOLDER');

  checkRole(role: string, has: boolean){
    has
      ? this.userHandlingForm.controls.roles.setValue([...this.userHandlingForm.controls.roles.value, role])
      : this.userHandlingForm.controls.roles.setValue(this.userHandlingForm.controls.roles.value.filter((r: string) => r !== role));
  }

  onSubmit() {

    this.userHandlingForm.updateValueAndValidity({onlySelf: true, emitEvent: false});

    if(this.userHandlingForm.invalid) {
      this.submitTried.set(true);
      return;
    }

    const isLoading = signal(true);

    this.dialog.open(LoaderElementComponent, {
        data: {
            loading: isLoading
        },
        disableClose: true,
        enterAnimationDuration: '300ms',
        exitAnimationDuration: '300ms',

    })

    //FIXME: do switch-case when time
    if(this.action === 'register') { this.http.post('/api/loginUser/registration', this.userHandlingForm.value).subscribe(()=>{
        isLoading.set(false);
        this.router.navigate(['/dashboard/login']);
    })}

    if(this.action === 'create') { this.http.post('/api/loginUser/registration', this.userHandlingForm.value).subscribe(()=>{
      isLoading.set(false);
      this.router.navigate(['/dashboard/users']);
    })}

    if(this.action === 'edit') { this.http.put('/api/loginUser', this.userHandlingForm.value).subscribe(()=>{
      isLoading.set(false)
      this.hasAdminAuth()
          ? this.router.navigate(['/dashboard/users'])
          : this.router.navigate(['/dashboard/']);
    }) }

  }

  //used in edit and create page
  onCompanySelect(company: any) {
    this.userHandlingForm.removeControl('company');
    if(company?.value === -1){
      (this.companySelect() as any).clear();
    }
    if(company?.value === 0){
      this.userHandlingForm.addControl('company', newCompanyHandlingForm() )
    }
    if(company?.value>0){ this.userHandlingForm.addControl('company', new FormControl(company.value));}
  }

  //used in registration page
  toggleCompanySelect(){
    this.checkBox.set(!this.checkBox());
    this.checkBox()
      ? this.userHandlingForm.addControl('company', newCompanyHandlingForm() )
      : this.userHandlingForm.removeControl('company');
  }

}
